import { encounterCreatureField, hasCampaignPermission, hasPlayerPermission, itemsCol, layerOf } from '../shared/array-types';
import { record, type Row } from '../janus-engine/ability/context';
import { activityPortEnv } from '../janus-engine/actions/activity';
import { casterCanReadWrittenLanguage } from '../janus-engine/port/k3-rules/luck_and_skill_roll_helpers';
import { renderWrittenDescriptionWithTranslate } from '../janus-engine/port/k5-render/luck_and_skill_roll_helpers';
import { iconPh, ptMount } from './card-shell';
import sourceCatalogs from '../janus-engine/port/k3-rules/source-catalogs.json';

interface WrittenInputs {
    suffix: string;
    description: string;
    language: string;
    is_inventory: boolean;
    item_id: string | null;
    inventory_id: string | null;
    item_type: string | null;
    caster_id: string | null;
    campaign_gm: string | null;
    viewer_id: string | null;
    viewer_is_admin: boolean;
    permissions?: Row | null;
    allow_plain: boolean;
    translate_label: string | null;
}
// Function shapes only: the forms bundle owns the cipher's runtime state/listeners.
type WrittenWindow = Window & {
    ftGetActiveCasterId?: () => string | null;
    ftSeedCasterFromEncounterCache?: (id: string) => boolean;
    fetchAndCacheCaster?: (id: string) => Promise<unknown>;
    ftLanguageGetCssClass?: (id: unknown) => string;
    ftLanguageRenderParsed?: (text: string, id: unknown, parse: (text: string) => string) => string;
    ftMountWrittenDescriptions?: (root?: Element | Document) => void;
};
const host = window as WrittenWindow;
const common = sourceCatalogs.Language.find(row => row.variable_name === 'common')!.item_id;
const skillsOf = (caster: unknown): Row | null => {
    const row = record(caster);
    return record(record(record(row?.computed)?.creature)?.skills) ?? record(row?.skills);
};
const skillEntry = (caster: unknown, name: unknown): Row | null => record(skillsOf(caster)?.[String(name)]);
// The page already compiled these skills, exactly as in the native book-reading adapter.
const literacyEnv = activityPortEnv({ layer_value: layerOf, build_caster_skills: () => {}, caster_skill_entry: skillEntry });

export function writtenDescriptionHtml(input: WrittenInputs, caster: Row | null): string {
    const language = input.language.trim().toUpperCase();
    const scripted = (id: string): boolean => !!host.ftLanguageGetCssClass!(id);
    const pc = !!itemsCol(record(caster?.player_characters), 'item_id')
        && !!encounterCreatureField(caster?.encounter_creatures, 'encounter_creature_id');
    const gm = input.is_inventory && input.item_type === 'book' && !pc && scripted(language) && language !== common
        && (input.viewer_is_admin || hasCampaignPermission(input) || !!input.viewer_id && input.viewer_id === input.campaign_gm);
    const env = activityPortEnv({
        'LanguageScript::isTranslatableScript': scripted,
        'LanguageScript::renderParsed': (raw: string, id: string) => host.ftLanguageRenderParsed!(raw, id, ptMount),
        caster_can_read_written_language: (row: unknown, id: string, tier: number) => casterCanReadWrittenLanguage(record(row), id, tier, literacyEnv),
        ft_parse_text_mount: ptMount,
        caster_skill_entry: skillEntry,
        icon_ph: iconPh,
        item_field: itemsCol,
        has_player_permission: hasPlayerPermission,
    });
    return renderWrittenDescriptionWithTranslate(input.suffix, input.description, language, input.is_inventory,
        caster, gm || input.allow_plain, gm ? 'GM Translate' : input.translate_label, env);
}

const mounts = new Map<HTMLElement, { input: WrittenInputs; html?: string; actor?: string }>();
const loading = new Map<string, Promise<unknown>>();
let actorTimer: ReturnType<typeof setInterval> | undefined;
function paint(mount: HTMLElement): void {
    const state = mounts.get(mount)!;
    if (!mount.isConnected) { mounts.delete(mount); return; }
    if (!host.ftLanguageRenderParsed) return; // forms aggregate attaches the cipher bridge later in the footer
    const id = String(host.ftGetActiveCasterId ? host.ftGetActiveCasterId() ?? '' : state.input.caster_id ?? '');
    state.actor = id;
    host.ftSeedCasterFromEncounterCache?.(id);
    const caster = host.FateTide?.casterCache?.get(id) ?? host.FateTide?.casterCache?.get(id.toUpperCase()) ?? null;
    const html = writtenDescriptionHtml(state.input, caster);
    const output = mount.querySelector<HTMLElement>('.ft-written-output')!;
    if (html !== state.html) {
        output.innerHTML = html;
        state.html = html;
        host.ftMountParsedText?.(output);
        host.ftMountParseTextTables?.(output);
    }
    if (id && !caster && host.fetchAndCacheCaster && !loading.has(id)) {
        const request = host.fetchAndCacheCaster(id);
        loading.set(id, request);
        void request.then(() => {
            for (const node of mounts.keys()) paint(node);
        }).catch(error => console.warn('written description caster load failed', error)).finally(() => loading.delete(id));
    }
}

export function ftMountWrittenDescriptions(root: Element | Document = document): void {
    const nodes = [...root.querySelectorAll<HTMLElement>('.ft-written-mount')];
    if (root instanceof HTMLElement && root.matches('.ft-written-mount')) nodes.unshift(root);
    for (const mount of nodes) {
        if (!mounts.has(mount)) {
            const input = JSON.parse(mount.querySelector('.ft-written-blob')!.textContent || 'null') as WrittenInputs;
            mounts.set(mount, { input });
        }
        paint(mount);
    }
    // The active-caster funnel has no change event when switching to an already-cached actor.
    // Watch only while cards exist; a switch immediately retires a previously visible translation.
    if (mounts.size && !actorTimer) actorTimer = setInterval(() => {
        for (const [mount, state] of mounts) {
            const id = String(host.ftGetActiveCasterId?.() ?? state.input.caster_id ?? '');
            if (!mount.isConnected || id !== state.actor) paint(mount);
        }
        if (!mounts.size) { clearInterval(actorTimer); actorTimer = undefined; }
    }, 100);
}

host.ftMountWrittenDescriptions = ftMountWrittenDescriptions;
new MutationObserver(changes => {
    for (const change of changes) for (const node of change.addedNodes) {
        if (node instanceof HTMLElement) ftMountWrittenDescriptions(node);
    }
}).observe(document.documentElement, { childList: true, subtree: true });
host.addEventListener('ft:caster-state-applied', () => { for (const mount of mounts.keys()) paint(mount); });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => ftMountWrittenDescriptions());
else ftMountWrittenDescriptions();
